/**
 * Theme-aware avatar / user colors.
 * Token names map to CSS vars in src/app/assets/theme-avatar-users.css
 * (--color-comp-avatar-user-*), overridden per .theme-light-1 / dark-1 / lsDark-1.
 * Safelist in src/app/assets/safelist-app.css must include these.
 *
 * Palette length is kept at 13 (legacy) so `hash % length` for existing names
 * stays on the same slot; each slot maps to the nearest of the 9 new hues.
 */
import { getNumberFromString } from '../../utils/string'

export const AVATAR_USER_COLOR_HUES = [
  'red',
  'orange',
  'lime',
  'forest',
  'cyan',
  'blue',
  'indigo',
  'purple',
  'pink',
] as const

export type AvatarUserColorHue = typeof AVATAR_USER_COLOR_HUES[number]

export type AvatarUserColorToken = `comp-avatar-user-${AvatarUserColorHue}`
export type AvatarUserSoftColorToken = `comp-avatar-user-${AvatarUserColorHue}-soft`
export type AvatarUserMidColorToken = `comp-avatar-user-${AvatarUserColorHue}-mid`

/**
 * Slot order matches the legacy 13-color palette:
 * brown, red, spanish-orange, satel-gold, olive, yellow-green,
 * green, teal, azure, satel-blue, purple, dark-pink, black-600
 * → nearest of the 9 theme hues (yellow/brown/black fold into neighbors).
 */
export const usersColors: AvatarUserColorToken[] = [
  'comp-avatar-user-red', // brown
  'comp-avatar-user-red', // red-shades-900
  'comp-avatar-user-orange', // spanish-orange
  'comp-avatar-user-orange', // satel-gold (yellow excluded)
  'comp-avatar-user-lime', // olive
  'comp-avatar-user-lime', // yellow-green-shades-700
  'comp-avatar-user-forest', // green
  'comp-avatar-user-cyan', // teal
  'comp-avatar-user-blue', // azure-shades-800
  'comp-avatar-user-indigo', // satel-blue
  'comp-avatar-user-purple', // purple
  'comp-avatar-user-pink', // dark-pink
  'comp-avatar-user-indigo', // black-600
]

/** Room/group avatars share the same theme-aware fill palette (same slot count). */
export const chatsColors: AvatarUserColorToken[] = usersColors

const toMid = (token: AvatarUserColorToken): AvatarUserMidColorToken =>
  `${token}-mid` as AvatarUserMidColorToken

/** Mid tones for icons / secondary accents on fills (parallel to usersColors). */
export const externalUsersColors: AvatarUserMidColorToken[] = usersColors.map(toMid)

export const audioColors: AvatarUserMidColorToken[] = externalUsersColors

export const userColorsMap: Record<string, string> = Object.fromEntries(
  AVATAR_USER_COLOR_HUES.map((hue) => [
    `comp-avatar-user-${hue}`,
    `comp-avatar-user-${hue}-soft`,
  ]),
)

export const audioTrackBackgroundsMap: Record<string, string> = Object.fromEntries(
  AVATAR_USER_COLOR_HUES.map((hue) => [
    `comp-avatar-user-${hue}`,
    `comp-avatar-user-${hue}-mid`,
  ]),
)

export const getColorByName = (name: string, isRoom?: boolean): AvatarUserColorToken => {
  const palette = isRoom ? chatsColors : usersColors
  const index = getNumberFromString(name) % palette.length
  return palette[index]!
}

export const getIconColorByName = (name: string): AvatarUserMidColorToken => {
  const index = getNumberFromString(name) % externalUsersColors.length
  return externalUsersColors[index]!
}

export const getSoftColorByName = (name: string): AvatarUserMidColorToken => {
  const index = getNumberFromString(name) % audioColors.length
  return audioColors[index]!
}

const variants = ['brand', 'positive', 'warning', 'negative', 'neut']

export const getVariantByName = (name: string) => {
  const index = getNumberFromString(name) % variants.length
  return variants[index] as 'brand' | 'positive' | 'warning' | 'negative' | 'neut'
}

export const useUserColor = () => {
  return {
    getColorByName,
  }
}
